/**
 * The supplier list's status vocabulary, in its own module so the list row and the results
 * panel read from one copy.
 *
 * It lived in `suppliers-table.tsx` until PLT-6269. The panel's header says what state the
 * supplier is in, and that has to be the same word the row it opened from says; importing
 * it from the table would have made a cycle, since the table is what renders the panel.
 */
import { SupplierAssessmentStatus } from '@upright/common/iam/organizations/organization-types';
import { ON_THE_WAY_SUPPLIER_STATUSES } from '@upright/common/iam/organizations/supplier-card';

/**
 * What each status is called on screen — product words, not the API's.
 *
 * Roni's vocabulary, 2026-09-10, after reading "Ready to review" as possibly meaning the
 * results were ready.
 *
 * Two things it fixes. The words now name what the *system* is doing to the company rather
 * than what stage a pipeline is in — "Modelling company", "Assessing risks" — so no state
 * can be read as a claim about results that do not exist. And the two failures are
 * separate: a company we could not model has nothing to assess, while a modelled company
 * whose assessment failed can be retried, and telling the reader which one they have is the
 * difference between the two next steps.
 *
 * "Products to review" and not "Products & sites to review": site identification runs
 * *inside* the publish job (`perform-publish-ipr-product-set.ts`), so at review time no
 * sites exist yet. Raised with Roni — making sites reviewable before assessment is a
 * pipeline change, not a label.
 */
export const STATUS_LABELS: Record<SupplierAssessmentStatus, string> = {
  'not-modelled': 'Company not modelled',
  modelling: 'Modelling company',
  'awaiting-review': 'Products to review',
  assessing: 'Assessing risks',
  assessed: 'Assessed',
  'modelling-failed': 'Company modelling failed',
  'assessment-failed': 'Assessment failed',
};

/**
 * Statuses on the way to an assessment, where the supplier's levels are still to come: the pipeline
 * is running, or it waits on the user (products to describe or to review). The two failures are not
 * on the way: nothing more comes without someone retrying.
 *
 * One copy for the Suppliers list, which puts these first, and the Supplier base grid, which says
 * why such a row has no levels yet.
 */
export const ON_THE_WAY_STATUSES = ON_THE_WAY_SUPPLIER_STATUSES;
